import React, { FC } from 'react'
import { t } from '@lingui/macro'
import { css } from '@emotion/react'

import { UserModelPublicBase } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { Upload } from '../../components/common/form/upload/Upload'
import IconTrash from '../../components/common/icon/IconTrash'
import { notification } from '../../components/common/notifications/Notifications'
import { UserIcon } from '../../components/common/user/user-icon/UserIcon'

import {
  useRemoveLoggedUsersProfileImagesMutation,
  useUpdateProfileImageMutation
} from './UserImage.codegen'
import { UserProfilePageDetailAsMeFragment } from './UserProfilePage.codegen'
import { Flex, ButtonIcon } from 'front-end/src/components'
import { brandColors } from '../../../../shared/basicPalette'

export const UserImage: FC<{
  isCurrentUser: boolean
  user: UserModelPublicBase | UserProfilePageDetailAsMeFragment
  handleUserForm: any
  canEdit: true
}> = ({ user, handleUserForm, canEdit, isCurrentUser }) => {
  const [uploadProfileImage, { loading: uploadProfileImageLoading }] =
    useUpdateProfileImageMutation()

  const [removeImage, { loading: isRemoving }] =
    useRemoveLoggedUsersProfileImagesMutation()

  return (
    <Flex
      align="flex-start"
      position="relative"
      className="User__image"
      mb="32px"
      css={css`
        .RemoveImage {
          transition: all 0.3s ease-in-out;
          opacity: 0;
        }
        :hover {
          .RemoveImage {
            opacity: 1;
          }
        }
      `}
    >
      <UserIcon user={user} size={160}>
        {canEdit && isCurrentUser && (
          <>
            <Upload
              css={css`
                button {
                  position: absolute;
                  right: 0;
                  left: 0;
                  bottom: -16px;
                  width: 100%;
                  background: white;
                }
              `}
              title={t`Upload image`}
              accept="image/png, image/jpeg"
              isLoading={uploadProfileImageLoading}
              onChange={async (e) => {
                const file = e.currentTarget.files
                  ? e.currentTarget.files[0]
                  : null

                if (file) {
                  const reader = new FileReader()
                  reader.readAsDataURL(file)
                  reader.onload = async (): Promise<void> => {
                    const uploadFile = {
                      name: file.name,
                      data: reader.result as string
                    }

                    try {
                      const { data } = await uploadProfileImage({
                        variables: { file: uploadFile }
                      })
                      if (data) {
                        const newImage = data.me.updateProfileImage
                        if (newImage) {
                          handleUserForm({
                            image_url: newImage
                          })
                        }
                      }
                    } catch (e) {
                      notification.error(t`Unable to upload cover image`)
                    }
                  }
                }
              }}
            />
          </>
        )}
      </UserIcon>
      {canEdit && isCurrentUser && user.image_url && (
        <ButtonIcon
          backgroundColor={brandColors.grey.lighter}
          className="RemoveImage"
          color={brandColors.grey.mid}
          hintProps={{
            placement: 'bottom'
          }}
          icon={<IconTrash />}
          aria-label={t`Remove image`}
          hint={t`Remove image`}
          onClick={async () => {
            await removeImage()
            handleUserForm({
              image_url: null
            })
          }}
          disabled={isRemoving}
        />
      )}
    </Flex>
  )
}
